/**
 * Tailwind class strings shared by the health-record screens (page, sheets, doctor panel, chat cards).
 * They replace healthRecord.css / healthRecordPage.css. Tokens: slate-* neutrals, primary, rounded-*.
 * Tailwind scans this file, so every class below is real literal text.
 */

import { toast } from "../../utils/notifications";
import cn from "@/common/utils/classNames";
import { toUserMessage } from "../../utils/userMessage";

export { cn };

export const notifyError = (message) => {
  if (!message) return;
  toast.error(typeof message === "string" ? toUserMessage(message) : message);
};

export const notifySuccess = (message) => {
  if (!message) return;
  toast.success(typeof message === "string" ? toUserMessage(message, "انجام شد.") : message);
};

/** The settings card: white, no border, sits on the settings grey. */
export const CARD = "overflow-hidden rounded-[14px] bg-white";

export const HR = {
  // ---- page frame ----
  page: "min-h-full overflow-x-hidden bg-[#F2F2F2] pb-[calc(96px+env(safe-area-inset-bottom,0px))] text-slate-900",
  top: "sticky top-0 z-10 bg-white",
  inner: "mx-auto w-full max-w-[760px]",
  body: "mx-auto flex w-full max-w-[760px] flex-col gap-4 px-3 pt-3 md:px-4 md:pt-5",
  head: "flex h-14 items-center gap-1 px-2",
  title: "m-0 ps-1 text-base font-medium text-slate-900",

  // ---- rounded filter chips ----
  chipsBase: "flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  chipsTop: "bg-white px-3 pb-2.5 md:px-4",
  chipsFilter: "px-1",
  chipsFree: "px-0",
  chip: "inline-flex min-h-10 flex-none items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 text-[13px] text-slate-700 transition-colors hover:border-slate-300",
  chipOn: "border-primary bg-primary text-white hover:border-primary",
  chipPerson: "ps-[5px]",
  chipSmall: "text-[12.5px]",
  miniAvatar: "inline-flex h-[30px] w-[30px] items-center justify-center rounded-full text-[13px] font-bold text-white",

  // ---- row groups ----
  block: "flex flex-col gap-1.5",
  blockHead: "flex items-center justify-between px-1",
  blockTitle: "m-0 px-3 text-[13px] font-semibold leading-5 text-primary",
  group: cn("m-0 list-none p-0", CARD),
  row: "relative flex min-h-[60px] items-center gap-1 pe-2 before:pointer-events-none before:absolute before:end-0 before:start-[68px] before:top-0 before:h-px before:bg-slate-100 before:content-[''] first:before:hidden has-[>button[data-hit]:not(:disabled)]:hover:bg-slate-50",
  rowStack: "flex-wrap",
  rowEmpty: "min-h-0 px-4 py-3.5 text-[13px] text-slate-500",
  rowHit: "flex min-w-0 flex-1 items-center gap-3 py-2.5 pe-1 ps-3.5 text-start text-inherit disabled:cursor-default",
  rowButton: "flex min-h-[60px] w-full items-center gap-3 px-3.5 py-2.5 text-start text-inherit hover:bg-slate-50",
  rowDetail: "basis-full px-4 pb-3.5 ps-[68px]",
  rowMain: "flex min-w-0 flex-1 flex-col gap-0.5",
  rowTitle: "text-[15px] font-normal leading-[1.6] text-[#222] [overflow-wrap:anywhere]",
  rowMeta: "text-[12.5px] leading-5 text-[#767c86]",
  caret: "me-1.5 flex-none text-slate-400 transition-transform",
  caretOpen: "rotate-180",
  note: "whitespace-pre-wrap text-sm leading-[1.9] text-slate-900",

  // ---- round icon next to a row ----
  avatar:
    "inline-flex h-[42px] w-[42px] flex-none items-center justify-center overflow-hidden rounded-xl bg-primary/10 text-base font-bold text-primary [&_img]:h-full [&_img]:w-full [&_img]:object-cover",
  avatarKind: {
    person: "rounded-full text-white",
    medication: "bg-teal-50 text-teal-600",
    measurement: "bg-rose-50 text-rose-600",
    condition: "bg-amber-50 text-amber-600",
    vaccine: "bg-violet-50 text-violet-600",
    note: "bg-slate-100 text-slate-500",
  },
  avatarThumb: "rounded-xl",
  thumbBtn:
    "inline-flex h-11 w-11 flex-none items-center justify-center overflow-hidden rounded-[10px] border border-slate-200 bg-slate-50 p-0 text-slate-500 [&_img]:h-full [&_img]:w-full [&_img]:object-cover",

  // ---- small badges ----
  badges: "mt-[3px] flex flex-wrap gap-1.5 empty:hidden",
  badge: "self-start whitespace-nowrap rounded-full border border-slate-100 bg-slate-50 px-2 text-[11px] leading-[18px] text-slate-500",
  badgeKind: {
    doctor_rx: "border-transparent bg-primary/10 text-primary",
    chat: "border-transparent bg-primary/10 text-primary",
    booking: "border-transparent bg-primary/10 text-primary",
    active: "border-transparent bg-emerald-500/10 text-emerald-700",
    edited: "border-transparent bg-amber-500/15 text-amber-700",
    draft: "border-transparent bg-amber-500/15 text-amber-700",
  },
  rxItems: "mt-0.5 flex flex-col gap-0.5",

  // ---- buttons ----
  iconBtn: "inline-flex h-12 w-12 flex-none items-center justify-center rounded-full text-slate-700 active:bg-black/[0.06]",
  itemMenu: "!h-10 !w-10 text-slate-400",
  textBtn: "min-h-10 flex-none rounded-lg px-3 text-sm font-semibold text-primary hover:bg-slate-50",
  textBtnFaint: "font-medium text-slate-500 opacity-80",
  textBtnDanger: "text-red-600",
  pdf: "mt-0.5 min-h-7 self-start p-0 no-underline",
  actions: "mt-2 flex gap-2",
  fab: "fixed bottom-[calc(20px+env(safe-area-inset-bottom,0px))] left-1/2 z-20 inline-flex min-h-12 -translate-x-1/2 items-center gap-1.5 rounded-full bg-primary px-6 text-[15px] font-bold text-white shadow-card md:left-[max(24px,calc(50%-380px+16px))] md:translate-x-0",

  // ---- summary ----
  notice: "rounded-2xl bg-primary/10 px-4 py-3.5",
  stats: cn("grid grid-cols-4 [@media(max-width:420px)]:grid-cols-2", CARD),
  stat: "flex min-h-[76px] flex-col items-center justify-center gap-0.5 border-slate-100 px-1 py-3 hover:bg-slate-50 [&:not(:first-child)]:border-s [@media(max-width:420px)]:[&:nth-child(3)]:border-s-0 [@media(max-width:420px)]:[&:nth-child(n+3)]:border-t",
  statN: "text-[22px] font-extrabold leading-[1.3] text-primary",
  statLabel: "text-center text-xs leading-normal text-slate-500",
  panel: cn("px-4 py-3.5", CARD),
  empty: "px-6 py-8 text-center text-sm leading-[1.9] text-slate-500",

  // ---- trend chart ----
  trend: "flex flex-col gap-2.5",
  trendHead: "flex flex-wrap items-baseline justify-between gap-2",
  trendTitle: "text-[15px] font-bold text-slate-900",
  trendValue: "text-[15px] text-slate-900",
  trendPlot: "w-full overflow-hidden",
  trendSvg: "block max-w-full [direction:ltr]",
  trendTick: "fill-slate-400 text-[11px]",
  trendLegend: "flex items-center gap-1.5 text-xs text-slate-500",
  trendKey: "inline-block h-[3px] w-3.5 rounded-sm bg-primary",
  trendKey2: "ms-2.5 bg-emerald-500",

  // ---- bottom sheets (AppSheetModal shell overrides + parts) ----
  sheet:
    "!border-0 !bg-white !p-0 text-slate-900 [&_[class*=scroll-region]]:!gap-0 [&_[class*=scroll-region]]:!pb-0",
  sheetHead: "flex min-h-14 items-center gap-1 px-2",
  sheetTitle: "min-w-0 flex-1 truncate ps-2 text-base font-medium text-slate-900",
  sheetFoot: "border-t border-slate-100 px-4 pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-2.5",
  groupSheet: "m-0 list-none bg-white p-0 pb-2 pt-1",
  groupSheetItem: "relative before:absolute before:end-0 before:start-[68px] before:top-0 before:h-px before:bg-slate-100 before:content-[''] first:before:hidden",

  // ---- forms ----
  form: "flex flex-col gap-3.5 p-4",
  field: "flex min-w-0 flex-col gap-1.5",
  fieldLabel: "text-[13px] font-semibold text-slate-500",
  input:
    "min-h-[46px] w-full rounded-[12px] border-0 bg-[#F2F2F2] px-3 py-2.5 text-[15px] text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10",
  textarea: "resize-y leading-[1.9]",
  pair: "grid grid-cols-2 gap-2.5",
  date: "grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-2",
  dateInput: "px-2 text-center",
  switchRow: "flex min-h-[52px] items-center justify-between gap-3 text-[15px]",
  pick: "flex min-h-14 w-full items-center gap-2.5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3.5 py-2.5 text-start text-sm text-primary",
  pickHas: "border-solid text-slate-900",
  pickText: "min-w-0 flex-1 truncate",
  pickChange: "flex-none text-[13px] text-primary",
  docEdit: "mt-3 flex flex-col gap-2",
  inlineLink: "self-start p-0 text-[12.5px] text-primary",
  hiddenMark: "ms-1.5 inline-flex align-[-2px] text-slate-400",
  dangerText: "text-red-600",

  // ---- "what can the doctor see" sheets ----
  hrs: "px-4 pb-2 pt-1 text-slate-900",
  hrsGroup: "border-b border-slate-100 py-1 last-of-type:border-b-0",
  hrsSwitchText: "flex flex-col leading-[1.6]",
  hrsSub: "text-xs text-slate-400",
  hrsVisits: "m-0 mb-1.5 list-none p-0",
  hrsVisitsNested: "ps-[30px]",
  hrsVisit: "flex min-h-[52px] cursor-pointer items-center gap-3 border-t border-slate-100 py-1.5",
  hrsVisitChild: "min-h-11 ps-[26px]",
  hrsCheck: "h-[18px] w-[18px] flex-none accent-primary",
  hrsVisitMain: "flex min-w-0 flex-col leading-[1.6]",
  hrsVisitTitle: "truncate text-sm",
  hrsNote: "mt-2.5 text-xs text-slate-400",
  hrsNoteTop: "mb-1 mt-2 text-[13px] text-slate-500",
  hrsSearch: "mb-1 mt-2",
  hrsAvatar: "!h-8 !w-8 flex-none !text-xs",
  hrsCatTitle: "mb-0.5 mt-3 text-[12.5px] font-bold text-slate-500",
  hrsFoot: "flex flex-col gap-2",

  // ---- chat access card / profile row ----
  accessCard: "pb-1 pt-0.5 text-center",
  accessTitle: "text-sm font-bold",
  accessSub: "mt-1 text-xs text-slate-500",
  accessActions: "mt-2.5 flex justify-center gap-2",
  accessMore: "mx-auto mt-2 block px-1 py-0.5 text-[12.5px] text-inherit opacity-55",
  pill: "mt-2 inline-block rounded-full bg-slate-500/10 px-2.5 py-[3px] text-xs text-slate-500",
  pillOk: "bg-emerald-500/10 text-emerald-700",
  muted: "text-xs text-slate-500",
  fileLine: "mt-[3px] px-1.5 text-[11.5px] text-slate-500 opacity-85",
  fileLineOpen: "p-0 text-primary",
  profile: "flex flex-col gap-3 rounded-[14px] bg-white px-4 py-3.5",
  profileLink: "flex items-center justify-between gap-2 text-inherit no-underline",
  profileText: "flex flex-col gap-0.5",
  profileTitle: "text-[15px] font-bold",
  moveItem: "flex items-center gap-3 rounded-[10px] p-3 text-start text-sm text-inherit hover:enabled:bg-primary/5",
  moveItemCurrent: "cursor-default text-primary",
  moveList: "flex flex-col px-2 pb-4 pt-1",
  moveTitle: "px-1 pb-2 text-[15px] font-bold",

  // ---- visit summary ----
  sum: "flex flex-col gap-2 rounded-2xl border border-slate-100 px-3 py-2.5",
  sumHead: "flex justify-between font-bold",
  sumLabel: "mb-0.5 text-[11.5px] font-bold text-slate-500",
  sumBody: "leading-[1.8]",
  sumList: "m-0 list-disc ps-[18px]",
  sumWarn: "rounded-[10px] bg-amber-500/10 px-2.5 py-1.5",
  sumWarnLabel: "text-amber-700",
  sumInsight: "rounded-[10px] bg-primary/5 px-2.5 py-1.5",
  sumInsightLabel: "text-primary",

  // ---- share card in chat ----
  shareCard: "min-w-[220px] max-w-[300px]",
  shareHit: "flex w-full items-center gap-2.5 py-0.5 text-start text-inherit",
  shareIcon: "inline-flex h-[38px] w-[38px] flex-none items-center justify-center rounded-xl bg-primary/10 text-primary",
  shareIconRevoked: "bg-slate-500/10 text-inherit opacity-60",
  shareMain: "flex min-w-0 flex-col leading-[1.6]",
  shareTitle: "truncate text-sm font-bold",
  shareSub: "text-xs opacity-70",
  shareThumb: "mt-2 block h-[120px] overflow-hidden rounded-[10px] bg-slate-500/10 [&_img]:block [&_img]:h-full [&_img]:w-full [&_img]:object-cover",
  shareLine: "mt-1.5 block text-[12.5px] leading-[1.7] opacity-85",
  shareMeasure: "mt-1.5 flex items-center justify-between gap-2",
  shareValue: "text-base font-bold",
  shareUnit: "text-[11.5px] opacity-70",
  shareSpark: "flex-none text-primary",
  shareFaint: "mt-1.5 text-[11px] opacity-55",
  shareOpenImage: "my-2 block w-full cursor-zoom-in overflow-hidden rounded-xl p-0 [&_img]:block [&_img]:w-full",
  shareOpenList: "my-2 list-disc ps-5 leading-loose",
  shareOpenText: "[&_p]:my-1.5 [&_p]:leading-[1.8]",
};
